import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/**
 * One header cell for {@link ../lib/useSortableTable}: the label is a button (click to sort,
 * toggling direction on repeat clicks), and a resize handle sits on the right edge whenever
 * `onResizeStart` is given. Pass the same props back from `useSortableTable` for every column that
 * should behave this way - see Accounts.tsx or PurchaseInvoices.tsx for a worked example.
 */
export function SortableTh<Key extends string>({
  label,
  sortKey,
  currentSortKey,
  sortDir,
  onSort,
  onResizeStart,
  className,
  style,
  scope = 'col',
}: {
  label: string;
  sortKey: Key;
  currentSortKey: Key | null;
  sortDir: 'asc' | 'desc';
  onSort: (key: Key) => void;
  onResizeStart?: (event: ReactMouseEvent<HTMLElement>) => void;
  className?: string;
  style?: CSSProperties;
  scope?: string;
}): JSX.Element {
  const active = currentSortKey === sortKey;
  const classes = [className, onResizeStart ? 'sortable-th-resizable' : null].filter(Boolean).join(' ');
  return (
    <th
      scope={scope}
      className={classes || undefined}
      style={style}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button type="button" className={active ? 'sortable-th-btn active' : 'sortable-th-btn'} onClick={() => onSort(sortKey)}>
        {label}
        <span className="sortable-th-arrow">{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </button>
      {onResizeStart ? <span className="sortable-th-handle" onMouseDown={onResizeStart} aria-hidden="true" /> : null}
    </th>
  );
}
